import {
  AtSign,
  CalendarClock,
  CalendarDays,
  CheckSquare,
  FileUp,
  Folder,
  Hash,
  Info,
  ListChecks,
  ListOrdered,
  MessageSquare,
  Phone,
  RotateCcw,
  Sliders,
  SlidersHorizontal,
  Table,
  Target,
  Type,
  Video,
  Mic,
} from 'lucide-react';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { EQuestionType, hasKey, EFormType } from '@hubertai/screening-question-types';
import type { TFormSubType, TAnyClientQuestionType } from '@hubertai/screening-question-types';
import { cn } from 'lib/utils';

const badgeMap = {
  [EQuestionType.Info]: {
    color: 'bg-gray-100 text-gray-800',
    icon: Info,
  },
  [EQuestionType.SingleSelect]: {
    color: 'bg-indigo-100 text-indigo-700',
    icon: Target,
  },
  [EQuestionType.MultipleSelect]: {
    color: 'bg-green-100 text-green-700',
    icon: ListChecks,
  },
  [EQuestionType.Open]: {
    color: 'bg-sky-100 text-sky-800',
    icon: MessageSquare,
  },
  [EFormType.Slider]: {
    color: 'bg-fuchsia-100 text-fuchsia-800',
    icon: SlidersHorizontal,
  },
  [EFormType.Range]: {
    color: 'bg-violet-100 text-violet-800',
    icon: Sliders,
  },
  [EFormType.Matrix]: {
    color: 'bg-cyan-100 text-cyan-800',
    icon: Table,
  },
  [EQuestionType.DatePicker]: {
    color: 'bg-amber-100 text-amber-800',
    icon: CalendarDays,
  },
  [EQuestionType.MonthPicker]: {
    color: 'bg-yellow-100 text-yellow-800',
    icon: CalendarClock,
  },
  [EFormType.Email]: {
    color: 'bg-teal-100 text-teal-800',
    icon: AtSign,
  },
  [EFormType.Text]: {
    color: 'bg-blue-100 text-blue-800',
    icon: Type,
  },
  [EFormType.Number]: {
    color: 'bg-indigo-100 text-indigo-800',
    icon: Hash,
  },
  [EFormType.Checkbox]: {
    color: 'bg-green-100 text-green-800',
    icon: CheckSquare,
  },
  [EFormType.File]: {
    color: 'bg-orange-100 text-orange-800',
    icon: FileUp,
  },
  [EQuestionType.Form]: {
    color: 'bg-purple-100 text-purple-800',
    icon: Folder,
  },
  [EQuestionType.Reusable]: {
    color: 'bg-purple-100 text-purple-800',
    icon: RotateCcw,
  },
  [EQuestionType.Ranking]: {
    color: 'bg-rose-100 text-rose-800',
    icon: ListOrdered,
  },
  [EQuestionType.Scheduling]: {
    color: 'bg-emerald-100 text-emerald-800',
    icon: CalendarClock,
  },
  [EQuestionType.Audio]: {
    color: 'bg-emerald-100 text-emerald-800',
    icon: Mic,
  },
  [EQuestionType.Video]: {
    color: 'bg-emerald-100 text-emerald-800',
    icon: Video,
  },
  [EFormType.Phone]: {
    color: 'bg-pink-100 text-pink-800',
    icon: Phone,
  },
};

interface Props extends ComponentProps<'div'> {
  question?: TAnyClientQuestionType;
  form?: TFormSubType;
  /** Render a standalone badge from a question type key (e.g. filter "single-select"). */
  typeKey?: string;
  /** Merged onto the badge itself, so a caller can mute or resize it (twMerge wins over the type colour). */
  className?: string;
}

const typeToKey = (type: string) => type.replace(/[-_]([a-z])/g, (_, c: string) => c.toUpperCase());

const QuestionTypeBadge = ({ question, form, typeKey, className }: Props) => {
  const { t } = useTranslation('Questionsv2');

  if (typeKey) {
    if (hasKey(badgeMap, typeKey)) {
      const { icon: Icon, color } = badgeMap[typeKey];

      return (
        <div
          className={cn(
            'inline-flex h-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
            color,
            className,
          )}
        >
          <Icon className="h-3 w-3" />
          {t(`questionTypes.labels.${typeToKey(typeKey)}`)}
        </div>
      );
    }

    return (
      <div
        className={cn(
          'inline-flex h-fit items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-800',
          className,
        )}
      >
        {t(`questionTypes.labels.${typeToKey(typeKey)}`)}
      </div>
    );
  }

  if (question) {
    if (question.type === EQuestionType.Form) {
      const badge = badgeMap[question.type];
      if (!badge) return null;

      const { icon: Icon, color } = badge;

      return (
        <div className="flex h-fit flex-col justify-between gap-4">
          <div
            className={cn(
              'inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
              color,
              className,
            )}
          >
            <Icon className="h-3 w-3" />
            {t(`questionTypes.labels.${typeToKey(question.type)}`)}
          </div>
          <div className="flex flex-row justify-between gap-0.5">
            {question.forms.slice(0, 2).map((form, index) => {
              return <QuestionTypeBadge key={index} form={form} />;
            })}
            {question.forms.length && question.forms.length > 2 ? (
              <span className="text-xs">{t('typeBadge.moreForms', { count: question.forms.length - 2 })}</span>
            ) : null}
          </div>
        </div>
      );
    } else {
      if (hasKey(badgeMap, question.type)) {
        const { icon: Icon, color } = badgeMap[question.type];

        return (
          <div
            className={cn(
              'inline-flex h-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
              color,
              className,
            )}
          >
            <Icon className="h-3 w-3" />
            {t(`questionTypes.labels.${typeToKey(question.type)}`)}
          </div>
        );
      } else {
        return null;
      }
    }
  } else if (form) {
    const badge = badgeMap[form.type];

    if (!badge) return null;

    const { icon: Icon, color } = badge;

    return (
      <div
        className={cn(
          'inline-flex h-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
          color,
          className,
        )}
      >
        <Icon className="h-3 w-3" />
        {t(`questionTypes.labels.${typeToKey(form.type)}`)}
      </div>
    );
  }

  return <div>{t('typeBadge.erroneousType')}</div>;
};

export default QuestionTypeBadge;
